<script lang="ts">
  import { Citas, Consultorios, type Cita, type CitaEstado, type Profesional } from '../lib/api';
  import { session } from '../lib/auth.svelte';
  import { tenantCtx } from '../lib/tenant.svelte';
  import { urlAgendarPublico } from '../lib/citas-publico';
  import HelpHint from './HelpHint.svelte';

  let error = $state<string | null>(null);
  let info  = $state<string | null>(null);
  let cargando = $state(false);

  function fail(e: any) { error = e?.message ?? String(e); info = null; }
  function ok(m: string) { info = m; error = null; }

  function hoyISO() { return new Date().toISOString().slice(0, 10); }
  let dia = $state(hoyISO());

  let citas = $state<Cita[]>([]);
  let profesionales = $state<Profesional[]>([]);

  const ESTADOS: CitaEstado[] = ['PROGRAMADA', 'CONFIRMADA', 'ATENDIDA', 'CANCELADA', 'NO_ASISTIO'];
  const ESTADO_LABEL: Record<CitaEstado, string> = {
    PROGRAMADA: 'Programada', CONFIRMADA: 'Confirmada', ATENDIDA: 'Atendida',
    CANCELADA: 'Cancelada', NO_ASISTIO: 'No asistió',
  };

  async function cargar() {
    cargando = true; error = null;
    try {
      citas = await Citas.listar(dia);
    } catch (e) { fail(e); }
    finally { cargando = false; }
  }

  $effect(() => { dia; cargar(); });
  $effect(() => {
    void Consultorios.profesionales().then(p => { profesionales = p; }).catch(() => { profesionales = []; });
  });

  function cambiarDia(deltaDias: number) {
    const d = new Date(dia + 'T00:00:00');
    d.setDate(d.getDate() + deltaDias);
    dia = d.toISOString().slice(0, 10);
  }

  function hora(iso: string) {
    try { return new Date(iso).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  }

  // ── Nueva cita ──────────────────────────────────────────────────────────
  function citaVacia(): Cita {
    return {
      pacienteNombre: '', telefono: '', profesional: '', consultorio: '',
      motivo: '', fechaHora: `${dia}T09:00`, duracionMin: 30, estado: 'PROGRAMADA',
    };
  }
  let mostrarForm = $state(false);
  let nueva = $state<Cita>(citaVacia());
  let editandoId = $state<string | null>(null);

  function abrirForm() { nueva = citaVacia(); editandoId = null; mostrarForm = true; }

  function toDatetimeLocal(iso: string) {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  function editar(c: Cita) {
    editandoId = c.id ?? null;
    nueva = {
      pacienteNombre: c.pacienteNombre, telefono: c.telefono ?? '', profesional: c.profesional ?? '',
      consultorio: c.consultorio ?? '', motivo: c.motivo ?? '',
      fechaHora: c.fechaHora ? toDatetimeLocal(c.fechaHora) : `${dia}T09:00`,
      duracionMin: c.duracionMin ?? 30, estado: c.estado ?? 'PROGRAMADA',
    };
    mostrarForm = true;
  }

  async function guardar(e: SubmitEvent) {
    e.preventDefault();
    try {
      const payload: Cita = { ...nueva, fechaHora: new Date(nueva.fechaHora).toISOString() };
      if (editandoId) {
        await Citas.actualizar(editandoId, payload);
      } else {
        await Citas.crear(payload);
      }
      ok(editandoId ? 'Cita actualizada.' : 'Cita agendada.');
      mostrarForm = false;
      editandoId = null;
      await cargar();
    } catch (ex) { fail(ex); }
  }

  async function cambiarEstado(c: Cita, estado: CitaEstado) {
    try { await Citas.cambiarEstado(c.id!, estado); await cargar(); }
    catch (ex) { fail(ex); }
  }

  async function recordar(c: Cita) {
    try {
      const r = await Citas.recordar(c.id!);
      if (r.enviado && r.simulado) ok('Recordatorio simulado (WhatsApp no configurado en este entorno).');
      else if (r.enviado) ok('Recordatorio enviado por WhatsApp.');
      else fail(r.error || 'No se pudo enviar el recordatorio.');
      await cargar();
    } catch (ex) { fail(ex); }
  }

  async function eliminar(c: Cita) {
    if (!confirm('¿Eliminar esta cita?')) return;
    try { await Citas.eliminar(c.id!); await cargar(); }
    catch (ex) { fail(ex); }
  }

  const linkPublico = $derived(
    session.value?.tenantId ? urlAgendarPublico(session.value.tenantId) : null,
  );

  /** Asistente de citas para pacientes: solo edicion clinica, no hospital. */
  const mostrarAsistentePaciente = $derived(
    !!linkPublico && !tenantCtx.isHospitalEdition,
  );

  function copiarLinkPublico() {
    if (!linkPublico) return;
    void navigator.clipboard.writeText(linkPublico).then(() => ok('Enlace del asistente copiado.'));
  }
</script>

<div class="agenda">
  <header class="head">
    <div class="nav">
      <button onclick={() => cambiarDia(-1)} type="button" title="Día anterior">‹</button>
      <input type="date" bind:value={dia} />
      <button onclick={() => cambiarDia(1)} type="button" title="Día siguiente">›</button>
      <button class="hoy" onclick={() => dia = hoyISO()} type="button">Hoy</button>
    </div>
    <button class="primary" onclick={abrirForm} type="button">+ Nueva cita</button>
    {#if mostrarAsistentePaciente}
      <button class="link-pub" type="button" onclick={copiarLinkPublico} title={linkPublico!}>
        Copiar enlace
      </button>
      <a class="link-pub open" href={linkPublico!} target="_blank" rel="noopener noreferrer">
        Abrir asistente
      </a>
    {/if}
  </header>

  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <HelpHint title="Guía rápida · Agenda">
    <p>Elija el profesional de la lista (Administración → Profesionales). No escriba el nombre a mano.</p>
    <ol>
      <li>Pulse <strong>+ Nueva cita</strong>.</li>
      <li>Complete paciente, hora y seleccione el médico en el desplegable.</li>
      <li>Si no aparece nadie, registre primero al profesional.</li>
    </ol>
  </HelpHint>

  {#if mostrarForm}
    <form class="card form" onsubmit={guardar}>
      <h3>{editandoId ? 'Editar cita' : 'Nueva cita'}</h3>
      <div class="grid">
        <label>Paciente / cliente *
          <input bind:value={nueva.pacienteNombre} required placeholder="Nombre completo" />
        </label>
        <label>WhatsApp / teléfono
          <input bind:value={nueva.telefono} type="tel" placeholder="+502 5555-1234" />
        </label>
        <label>Fecha y hora *
          <input bind:value={nueva.fechaHora} type="datetime-local" required />
        </label>
        <label>Duración (min)
          <input bind:value={nueva.duracionMin} type="number" min="5" step="5" />
        </label>
        <label>Profesional
          <select bind:value={nueva.profesional}>
            <option value="">— Seleccionar profesional —</option>
            {#each profesionales as p (p.id)}
              <option value={p.nombre}>{p.nombre}{p.colegiado ? ` · Col. ${p.colegiado}` : ''}</option>
            {/each}
            {#if nueva.profesional && !profesionales.some(p => p.nombre === nueva.profesional)}
              <option value={nueva.profesional}>{nueva.profesional}</option>
            {/if}
          </select>
          {#if profesionales.length === 0}
            <small class="muted">Registre profesionales en Administración → Profesionales.</small>
          {/if}
        </label>
        <label>Consultorio / sala
          <input bind:value={nueva.consultorio} placeholder="Consultorio 1" />
        </label>
        <label class="wide">Motivo
          <input bind:value={nueva.motivo} placeholder="Motivo de la cita" />
        </label>
      </div>
      <div class="acciones">
        <button type="button" class="ghost" onclick={() => { mostrarForm = false; editandoId = null; }}>Cancelar</button>
        <button type="submit" class="primary">{editandoId ? 'Guardar cambios' : 'Agendar'}</button>
      </div>
    </form>
  {/if}

  {#if cargando}
    <p class="muted">Cargando agenda…</p>
  {:else if citas.length === 0}
    <p class="muted">No hay citas para este día.</p>
  {:else}
    <div class="table-wrap">
      <table>
        <thead>
          <tr><th>Hora</th><th>Paciente</th><th>Motivo</th><th>Profesional</th><th>Consultorio</th><th>Teléfono</th><th>Estado</th><th>Acciones</th></tr>
        </thead>
        <tbody>
          {#each citas as c (c.id)}
            <tr class="estado-{c.estado}">
              <td class="hora">{hora(c.fechaHora)}<small>{c.duracionMin ?? 30}'</small></td>
              <td><strong>{c.pacienteNombre}</strong>
                {#if c.recordatorioEnviado}<span class="recok">✓ 24h</span>{/if}
                {#if c.recordatorioMatutinoEnviado}<span class="recok">✓ 7am</span>{/if}
              </td>
              <td>{c.motivo ?? '—'}</td>
              <td>{c.profesional ?? '—'}</td>
              <td>{c.consultorio ?? '—'}</td>
              <td>{c.telefono ?? '—'}</td>
              <td>
                <select value={c.estado} onchange={(e) => cambiarEstado(c, (e.currentTarget as HTMLSelectElement).value as CitaEstado)}>
                  {#each ESTADOS as s}<option value={s}>{ESTADO_LABEL[s]}</option>{/each}
                </select>
              </td>
              <td class="acc">
                <button class="wa" type="button" onclick={() => recordar(c)} disabled={!c.telefono} title="Enviar recordatorio por WhatsApp">WhatsApp</button>
                <button class="edit" type="button" onclick={() => editar(c)} title="Editar">✎</button>
                <button class="del" type="button" onclick={() => eliminar(c)} title="Eliminar">🗑</button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<style>
  .agenda { max-width: 920px; }
  .head { display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-bottom: 1rem; flex-wrap: wrap; }
  .nav { display: flex; align-items: center; gap: .4rem; }
  .nav button { border: 1px solid #e5e7eb; background: #fff; border-radius: 8px; padding: .4rem .7rem; cursor: pointer; font: inherit; color: #111827; }
  .nav .hoy { color: #15803d; font-weight: 600; }
  .nav input[type=date] { padding: .4rem .6rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; background: #fff; color: #111827; }
  .primary { background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border: 0; border-radius: 8px; padding: .55rem .9rem; font: inherit; font-weight: 600; cursor: pointer; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  .primary:hover { transform: translateY(-1px); }
  .link-pub { background: #fff; color: #15803d; border: 1px solid #16a34a; border-radius: 8px; padding: .55rem .9rem; font: inherit; font-weight: 600; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center; }
  .link-pub:hover { background: #f0fdf4; }
  .ghost { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: .55rem .9rem; cursor: pointer; font: inherit; color: #111827; }
  .err { color: #dc2626; } .ok { color: #166534; } .muted { color: #6b7280; }

  .card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1rem 1.25rem; margin-bottom: 1.25rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .form h3 { margin: 0 0 .8rem; color: #166534; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: .8rem; }
  .grid .wide { grid-column: 1 / -1; }
  label { display: block; font-size: .72rem; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; }
  input, select { display: block; width: 100%; box-sizing: border-box; padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; margin-top: .25rem; background: #fff; color: #111827; }
  input:focus, select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .acciones { display: flex; justify-content: flex-end; gap: .6rem; margin-top: 1rem; }

  .table-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { border-top: 1px solid #f3f4f6; padding: .5rem .8rem; text-align: left; color: #111827; }
  tr.estado-CANCELADA { opacity: .6; }
  tr.estado-NO_ASISTIO { opacity: .7; }
  td.hora { font-weight: 700; color: #15803d; white-space: nowrap; }
  td.hora small { display: block; color: #6b7280; font-weight: 400; }
  td select { padding: .35rem .5rem; border: 1px solid #e5e7eb; border-radius: 6px; font: inherit; background: #fff; color: #111827; }
  .recok { color: #166534; font-size: .72rem; margin-left: .4rem; white-space: nowrap; }
  .acc { white-space: nowrap; }
  .wa { background: #16a34a; color: #fff; border: 0; border-radius: 8px; padding: .4rem .65rem; cursor: pointer; font: inherit; font-size: .82rem; font-weight: 600; }
  .wa:disabled { background: #bbf7d0; cursor: not-allowed; }
  .del { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: .35rem .5rem; cursor: pointer; }
  .edit { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: .35rem .5rem; cursor: pointer; }
  @media (max-width: 620px) { .grid { grid-template-columns: 1fr; } }
</style>
